/**
 * Sidebar navigation model (design §Sidebar). Badge counts are wired to
 * real queue data in Phase 1; badgeVar names which count each item shows.
 */
import { isDemo } from '../demo/flag';

export type BadgeVar = 'stopped' | 'documents' | 'unassigned' | 'payments';

export interface NavItem {
  label: string;
  path: string;
  badgeVar?: BadgeVar;
  badgeColor?: string; // CSS var reference
  /** Shown only to OPS_MANAGER (the API answers 403 for anyone else). */
  managerOnly?: boolean;
}

/** The ops "Today" board (#299): demo world only until rhino-api ships GET /admin/work/today. */
const TODAY: NavItem = { label: 'Today', path: '/today' };
/** Profit views (#300): demo world only until rhino-api ships GET /admin/profit/summary; managers only (P9). */
const PROFIT: NavItem = { label: 'Profit', path: '/profit', managerOnly: true };

export const NAV_ITEMS: NavItem[] = [
  ...(isDemo() ? [TODAY] : []),
  { label: 'Dashboard', path: '/' },
  { label: 'My Work', path: '/work' },
  {
    label: 'Unassigned',
    path: '/unassigned',
    badgeVar: 'unassigned',
    badgeColor: 'var(--badge-red)',
  },
  { label: 'Orders', path: '/orders' },
  { label: 'Shipments', path: '/shipments' },
  {
    label: 'Documents',
    path: '/documents',
    badgeVar: 'documents',
    badgeColor: 'var(--badge-red)',
  },
  { label: 'Interests', path: '/interests' },
  { label: 'Payments', path: '/payments', badgeVar: 'payments', badgeColor: 'var(--badge-red)' },
  {
    label: 'Stopped trucks',
    path: '/stopped',
    badgeVar: 'stopped',
    badgeColor: 'var(--badge-red)',
  },
  { label: 'Drivers', path: '/drivers' },
  { label: 'Shippers', path: '/shippers' },
  { label: 'Partners', path: '/fleets' },
  { label: 'Accounts', path: '/accounts' },
  { label: 'Service lanes', path: '/service-lanes' },
  { label: 'Analytics', path: '/analytics' },
  ...(isDemo() ? [PROFIT] : []),
  { label: 'Settings', path: '/settings' },
];

/** Resolves the sidebar badge count for a nav item from the queue counts. */
export function navBadgeCount(item: NavItem, counts: Partial<Record<BadgeVar, number>>): number {
  return item.badgeVar ? (counts[item.badgeVar] ?? 0) : 0;
}

export const PAGE_META: Record<string, { title: string; subtitle: string }> = {
  '/work': { title: 'My Work', subtitle: 'Open orders you own — stopped trucks first, then oldest' },
  '/unassigned': {
    title: 'Unassigned',
    subtitle: 'Open orders nobody owns yet — claim to take them on',
  },
  '/today': { title: 'Today', subtitle: 'What is waiting, oldest first' },
  '/': { title: 'Dashboard', subtitle: 'Marketplace overview and queues needing attention' },
  '/orders': { title: 'Orders', subtitle: 'Draft and posted shipments awaiting bids/award' },
  '/interests': {
    title: 'Partner interests',
    subtitle: 'Manage inbound load interest expressions, follow-ups, and bid conversions',
  },
  '/shipments': { title: 'Shipments', subtitle: 'All shipments across the marketplace' },
  '/payments': {
    title: 'Payments',
    subtitle: 'Money recording, approvals, payouts and collections',
  },
  '/stopped': { title: 'Stopped trucks', subtitle: 'Trucks that stopped on a trip, oldest stop first' },
  '/documents': { title: 'Documents', subtitle: 'Compliance documents awaiting review' },
  '/drivers': { title: 'Drivers', subtitle: 'Driver accounts and KYC' },
  '/shippers': { title: 'Shippers', subtitle: 'Shipper accounts and KYC' },
  '/fleets': { title: 'Partners', subtitle: 'Partner accounts and verification' },
  '/accounts': {
    title: 'Accounts',
    subtitle: 'Unified identity accounts and cross-app persona registry',
  },
  '/service-lanes': {
    title: 'Service lanes',
    subtitle: 'Search partners by service lane and truck capacity',
  },
  '/analytics': {
    title: 'Analytics',
    subtitle: 'Liquidity and reliability trends for a date range',
  },
  '/profit': { title: 'Profit', subtitle: 'Most profit, not most business' },
  '/help': { title: 'Help', subtitle: 'How to use the ops console — search, or pick a topic' },
  '/settings/approval-limits': {
    title: 'Approval limits',
    subtitle: 'Who can approve how much, and the advance limit per lane',
  },
  '/settings': {
    title: 'Settings',
    subtitle: 'Platform configuration — margins, policies and the ops team',
  },
};
